import React from 'react';
import { KalturaPlayerEmbedParams, KalturaPlayerInitParams, KalturaPlayerVideoParams } from '../KalturaPlayer';

interface ContextValue
  extends Readonly<{
    getContactUsUrl: () => string;
    getKalturaPlayerInitParams: () => KalturaPlayerInitParams;
    getKalturaCase1VideoEmbedParams: () => KalturaPlayerEmbedParams;
    getKalturaCase2VideoEmbedParams: () => KalturaPlayerEmbedParams;
    getKalturaCase3VideoEmbedParams: () => KalturaPlayerEmbedParams;
  }> {}

const Context = React.createContext<ContextValue>({
  getContactUsUrl: () => {
    throw new Error('getContactUsUrl: ConfigContextProvider not found!');
  },
  getKalturaPlayerInitParams: () => {
    throw new Error('getKalturaPlayerInitParams: ConfigContextProvider not found!');
  },
  getKalturaCase1VideoEmbedParams: () => {
    throw new Error('getKalturaCase1VideoEmbedParams: ConfigContextProvider not found!');
  },
  getKalturaCase2VideoEmbedParams: () => {
    throw new Error('getKalturaCase2VideoEmbedParams: ConfigContextProvider not found!');
  },
  getKalturaCase3VideoEmbedParams: () => {
    throw new Error('getKalturaCase3VideoEmbedParams: ConfigContextProvider not found!');
  },
});

interface ConfigContextProviderProps
  extends Readonly<{
    contactUsUrl: string;
    kalturaPlayerInitParams: KalturaPlayerInitParams;
    kalturaPlayerCase1VideoParams: KalturaPlayerVideoParams;
    kalturaPlayerCase2VideoParams: KalturaPlayerVideoParams;
    kalturaPlayerCase3VideoParams: KalturaPlayerVideoParams;
    children?: React.ReactNode;
  }> {}

export const ConfigContextProvider = React.memo<ConfigContextProviderProps>((props) => {
  const {
    contactUsUrl,
    kalturaPlayerInitParams,
    kalturaPlayerCase1VideoParams,
    kalturaPlayerCase2VideoParams,
    kalturaPlayerCase3VideoParams,
    children,
  } = props;

  const GetContactUsUrlCallback = React.useCallback<() => string>(() => contactUsUrl, [contactUsUrl]);

  const GetKalturaPlayerInitParamsCallback = React.useCallback<() => KalturaPlayerInitParams>(() => kalturaPlayerInitParams, [
    kalturaPlayerInitParams,
  ]);

  const GetKalturaCaseVideoEmbedParamsCallback = React.useCallback(
    (videoParams: KalturaPlayerVideoParams) => {
      const params: KalturaPlayerEmbedParams = {
        partnerId: kalturaPlayerInitParams.partnerId,
        playerId: kalturaPlayerInitParams.playerId,
        ...videoParams,
      };

      return params;
    },
    [kalturaPlayerInitParams]
  );

  const GetKalturaCase1VideoEmbedParamsCallback = React.useCallback(
    () => GetKalturaCaseVideoEmbedParamsCallback(kalturaPlayerCase1VideoParams),
    [kalturaPlayerCase1VideoParams, GetKalturaCaseVideoEmbedParamsCallback]
  );

  const GetKalturaCase2VideoEmbedParamsCallback = React.useCallback(
    () => GetKalturaCaseVideoEmbedParamsCallback(kalturaPlayerCase2VideoParams),
    [kalturaPlayerCase2VideoParams, GetKalturaCaseVideoEmbedParamsCallback]
  );

  const GetKalturaCase3VideoEmbedParamsCallback = React.useCallback(
    () => GetKalturaCaseVideoEmbedParamsCallback(kalturaPlayerCase3VideoParams),
    [kalturaPlayerCase3VideoParams, GetKalturaCaseVideoEmbedParamsCallback]
  );

  const ContextValue = React.useMemo(() => {
    const contextValue: ContextValue = {
      getContactUsUrl: GetContactUsUrlCallback,
      getKalturaPlayerInitParams: GetKalturaPlayerInitParamsCallback,
      getKalturaCase1VideoEmbedParams: GetKalturaCase1VideoEmbedParamsCallback,
      getKalturaCase2VideoEmbedParams: GetKalturaCase2VideoEmbedParamsCallback,
      getKalturaCase3VideoEmbedParams: GetKalturaCase3VideoEmbedParamsCallback,
    };

    return contextValue;
  }, [
    GetContactUsUrlCallback,
    GetKalturaPlayerInitParamsCallback,
    GetKalturaCase1VideoEmbedParamsCallback,
    GetKalturaCase2VideoEmbedParamsCallback,
    GetKalturaCase3VideoEmbedParamsCallback,
  ]);

  return <Context.Provider value={ContextValue}>{children}</Context.Provider>;
});

export const useConfigContext = () => React.useContext<ContextValue>(Context);
